/**
 * ①業務マップ（F1）をルートへ繋ぐ受け口。
 *
 * **ビュー本体は入力を prop で受け取る形に作ってある。** その prop を URL から埋めるのが
 * この受け口の役目で、ビュー本体は URL を知らないままでいる。
 *
 * **①業務マップは旧の 2 本（業務ツリー `/epics` と業務の一覧 `/businesses`）を畳んだ先である。**
 * 旧の 2 本はルートごと消してあるので、この受け口が唯一の入口になる。
 *
 * **案件を検索条件（`?pj=`）で受け取る。** 案件が読めたら、その案件の変更の行を揃えてから
 * 渡し、印（追加 / 変更 / 削除）を出す。案件を落とさないのは、旧の 2 本が `?pj=` を読んでいた
 * 相手（`PJ_SEARCH_ROUTES`）をそのまま引き継ぐからである。落とすと、案件を選んだまま業務ツリーを
 * 開いていた利用者が、畳んだ先で黙ってマスターへ落ちる。
 *
 * **読取が揃うまで描かない**（案件の変更の行 1 本）。空の配列を「案件の変更 0 件」として
 * 渡すと、読取が途中であることと案件が何も触っていないことが見分けられなくなる
 * （`OverviewPage` の docblock）。読み込み中は骨組み、失敗は失敗として出す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの業務マップのビューを、URL から埋めた入力とともにルートへ繋ぐ。
 * @graph-connects OverviewPage [calls] 業務マップの描画
 * @graph-connects usePjChanges [calls] 案件の変更の行の取得
 * @graph-connects usePjSearchNumber [calls] 検索条件から案件番号を読む
 */
import type { PjChangeRow } from '@cortex/domain-spec-console';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '../../components/spec/fallback-states.js';
import { usePjChanges } from '../../hooks/queries/spec-graph-element-pj-queries.js';
import { OverviewPage } from '../OverviewPage.js';
import { usePjSearchNumber } from './pj-number.js';

/**
 * マスターを描いている間の、案件の変更の行。
 *
 * マスターには重ねる案件が無いので 1 行も無い。描画のたびに配列を作ると、`useMemo` で
 * 索引を組んでいる側（`buildPjTouchedIndex`）が毎回組み直す。
 * @graph-connects none
 */
const MASTER_HAS_NO_PJ_CHANGES: readonly PjChangeRow[] = [];

/**
 * ①業務マップのルートの中身。案件が読めればその姿を、読めなければマスターの姿を描く。
 *
 * **案件は URL から落とさない。** この画面は旧の業務ツリー（`/epics`）と業務の一覧
 * （`/businesses`）を畳んだ先で、どちらも `?pj=` を読んでいた。落とすと、案件を選んだまま
 * 旧の入口を開いていた利用者が畳んだ先で黙ってマスターへ落ちる。
 *
 * **案件を読む部分を別の部品に分ける。** 案件の有無で読取（`usePjChanges`）を投げるか
 * 決めるので、同じ部品に置くとフックの数が描画のたびに変わる。
 *
 * @returns 業務マップの画面
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップのルートへ、検索条件の案件が読めればその姿を、読めなければマスターの姿を描く。
 * @graph-connects OverviewPage [calls] 業務マップの描画
 * @graph-connects usePjSearchNumber [calls] 検索条件から案件番号を読む
 */
export function BusinessMapRoutePage() {
  const pjNumber = usePjSearchNumber();

  if (pjNumber === null) return <OverviewPage pjChanges={MASTER_HAS_NO_PJ_CHANGES} />;

  return <BusinessMapPjBody pjNumber={pjNumber} />;
}

/** `BusinessMapPjBody` の props。 @graph-connects none */
interface BusinessMapPjBodyProps {
  /** 検索条件から読んだ案件番号（1 以上の整数） */
  pjNumber: number;
}

/**
 * ①業務マップの案件を重ねた姿。変更の行が揃ってからビューへ渡す。
 *
 * **揃うまで描かない。** 空の配列を渡すと、読取が途中であることと案件が何も触っていない
 * ことが見分けられなくなる（`OverviewPage` の docblock）。
 *
 * @param props 案件番号
 * @returns 読み込み中 / 失敗 / 案件を重ねた業務マップ
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更の行を揃え、読み込み中・失敗・本体の 3 状態で業務マップを出し分ける。
 * @graph-connects OverviewPage [calls] 業務マップの描画
 * @graph-connects usePjChanges [calls] 案件の変更の行の取得
 */
function BusinessMapPjBody({ pjNumber }: BusinessMapPjBodyProps) {
  const { t } = useTranslation();
  const changes = usePjChanges(pjNumber);

  if (changes.error !== null && changes.error !== undefined)
    return <ErrorState error={changes.error} />;

  if (changes.data === undefined) {
    return (
      <div aria-busy="true" aria-label={t('common.loading')}>
        <Skeleton variant="rectangular" height="620px" />
      </div>
    );
  }

  return <OverviewPage pjNumber={pjNumber} pjChanges={changes.data.changes} />;
}
